/* ====================================================================
   SITE-WIDE MEMBERSHIP DEFAULTS (client)

   The three option lists behind the membership form — age groups,
   membership types, player types — as a super-admin has set them under
   Site Admin → Settings → Membership. The server owns them
   (server/siteDefaults.js); this is a cached read of one public endpoint,
   shared by every screen that renders one of those dropdowns.

   Fetched once per page load and held in module scope: half a dozen
   components need these lists, and none of them should each cost a
   request. Until the fetch lands, callers get the shipped constants, so a
   dropdown never renders empty.
   ==================================================================== */

import React from 'react';
import {
  AGE_GROUP_OPTIONS,
  DEFAULT_MEMBERSHIP_TYPE_OPTIONS,
  DEFAULT_PLAYER_TYPE_OPTIONS,
} from '../constants/membership';

export const SITE_MEMBERSHIP_DEFAULT_FALLBACK = {
  ageGroupOptions: AGE_GROUP_OPTIONS,
  membershipTypeOptions: DEFAULT_MEMBERSHIP_TYPE_OPTIONS,
  playerTypeOptions: DEFAULT_PLAYER_TYPE_OPTIONS,
};

const cleanList = (value, fallback) => {
  if (!Array.isArray(value)) return fallback;
  const out = value.filter(v => typeof v === 'string' && v.trim()).map(v => v.trim());
  return out.length ? out : fallback;
};

export const normalizeSiteMembershipDefaults = (raw) => {
  const src = (raw && typeof raw === 'object') ? raw : {};
  return {
    ageGroupOptions: cleanList(src.ageGroupOptions, SITE_MEMBERSHIP_DEFAULT_FALLBACK.ageGroupOptions),
    membershipTypeOptions: cleanList(src.membershipTypeOptions, SITE_MEMBERSHIP_DEFAULT_FALLBACK.membershipTypeOptions),
    playerTypeOptions: cleanList(src.playerTypeOptions, SITE_MEMBERSHIP_DEFAULT_FALLBACK.playerTypeOptions),
  };
};

let cached = null;
let inflight = null;
const listeners = new Set();

const publish = (value) => {
  cached = value;
  listeners.forEach((fn) => { try { fn(value); } catch { /* a dead listener must not stop the rest */ } });
  return value;
};

export const getSiteMembershipDefaults = () => cached || SITE_MEMBERSHIP_DEFAULT_FALLBACK;

/* A save from the super-admin panel calls this so every open dropdown
   updates without a reload. */
export const setSiteMembershipDefaults = (raw) => publish(normalizeSiteMembershipDefaults(raw));

export const loadSiteMembershipDefaults = (force = false) => {
  if (cached && !force) return Promise.resolve(cached);
  if (inflight && !force) return inflight;
  inflight = fetch('/api/auth/site-membership-defaults')
    .then(r => (r.ok ? r.json() : null))
    .then(data => publish(normalizeSiteMembershipDefaults(data)))
    .catch(() => publish(normalizeSiteMembershipDefaults(null)))
    .finally(() => { inflight = null; });
  return inflight;
};

export function useSiteMembershipDefaults() {
  const [defaults, setDefaults] = React.useState(getSiteMembershipDefaults);
  React.useEffect(() => {
    let alive = true;
    listeners.add(setDefaults);
    loadSiteMembershipDefaults().then(v => { if (alive) setDefaults(v); });
    return () => { alive = false; listeners.delete(setDefaults); };
  }, []);
  return defaults;
}
